import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { site } from "@/config/site";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updatedOn="April 2026">
      <p className="italic text-navy/60">
        This policy is a draft prepared with the DPDP Act, 2023 in mind. It has not
        been reviewed by a lawyer. Please have it reviewed before relying on it.
      </p>

      <h2>1. Who we are</h2>
      <p>
        VaultSafe is operated by <strong>{site.legalEntity}</strong>, {site.address.line1},{" "}
        {site.address.line2}. You can contact us at{" "}
        <a href={`mailto:${site.email}`}>{site.email}</a>.
      </p>

      <h2>2. What we collect</h2>
      <ul>
        <li>
          <strong>Account information you give us:</strong> name, business name, email
          address, phone number, address, and GSTIN.
        </li>
        <li>
          <strong>Payment information:</strong> processed by Razorpay. We do not
          store your card details on our servers.
        </li>
        <li>
          <strong>Technical data:</strong> IP address, device name, operating
          system version, backup logs, and storage used.
        </li>
        <li>
          <strong>The contents of the files you back up.</strong>
        </li>
      </ul>

      <h2>3. What we do with your backed-up files</h2>
      <p>
        We store your files, encrypted, so we can restore them to you on request.
        We do not read them, mine them, analyse them, or share them with any third
        party. Access is limited to the staff who need it to run and support the
        service, and all such access is logged.
      </p>

      <h2>4. Why we process your data</h2>
      <p>
        To deliver the service you have signed up for, to bill you, to support
        you, and to meet our legal obligations.
      </p>

      <h2>5. Who we share your data with</h2>
      <p>
        Only the service providers we need to run VaultSafe: our payment processor
        (Razorpay), our cloud infrastructure provider, and our email service
        provider. We never sell your data.
      </p>

      <h2>6. Where your data is stored</h2>
      <p>
        Your backup data is stored on <strong>encrypted, enterprise-grade cloud
        infrastructure across multiple secure, geographically-redundant data
        centres</strong>. The data centres we use hold internationally
        recognised information security certifications, including{" "}
        <strong>ISO/IEC 27001</strong>. Copies of your data are held in more
        than one physical location for resilience against outages. Your data is
        encrypted in transit and at rest, and access is limited to the staff
        who need it to run and support the service, and all such access is
        logged. Specific details about the storage location for your account
        can be provided on written request for compliance or audit purposes.
      </p>

      <h2>7. How long we keep your data</h2>
      <p>
        We keep your data for as long as your account is active. After cancellation
        or termination, we hold your backup data for a stated grace period (see our
        Terms) before it is permanently deleted.
      </p>

      <h2>8. How we secure your data</h2>
      <p>
        Encryption in transit and at rest, role-based access controls, and
        monitoring of access to backup infrastructure.
      </p>

      <h2 id="your-rights">9. Your rights — access, correction, deletion, and portability</h2>
      <p>
        Under the Digital Personal Data Protection Act, 2023, you have the right to:
      </p>
      <ul>
        <li><strong>Access</strong> a copy of the personal data we hold about you</li>
        <li><strong>Correct</strong> or update any information that is inaccurate or incomplete</li>
        <li><strong>Erase</strong> your personal data and the files you have backed up</li>
        <li><strong>Withdraw consent</strong> to processing at any time (this may end our ability to deliver the service)</li>
        <li><strong>Nominate</strong> another individual to exercise these rights on your behalf in the event of your death or incapacity</li>
        <li><strong>Grievance redressal</strong> if you believe your rights have been violated</li>
      </ul>

      <div
        className="my-6 rounded-xl border-l-4 border-brand bg-brand/5 p-5"
        data-testid="data-request-callout"
      >
        <p className="mb-2 font-semibold text-navy">How to make a data access or deletion request</p>
        <p className="mb-3 text-sm">
          Email <a href="mailto:grievance@vaultsafe.in" data-testid="data-request-email"><strong>grievance@vaultsafe.in</strong></a> from
          the address registered on your VaultSafe account with the subject line{" "}
          <em>"Data Request — [Access / Correction / Deletion / Export]"</em>. Please include:
        </p>
        <ul className="mb-3 text-sm">
          <li>Your full name and registered business name</li>
          <li>Account email and phone number</li>
          <li>The specific action you want us to take</li>
          <li>Any files or records you want included or excluded</li>
        </ul>
        <p className="mb-0 text-sm">
          <strong>What happens next:</strong> we acknowledge your request within{" "}
          <strong>{site.grievanceOfficer.responseHours}</strong> and complete it within{" "}
          <strong>30 days</strong>. For deletion requests, we verify your identity, remove the data
          from live systems immediately, and purge it from encrypted backup replicas within 30 days.
          You will receive a written confirmation when the request is complete.
        </p>
      </div>

      <h2>10. Grievance officer</h2>
      <p>
        As required under the DPDP Act, 2023:<br />
        <strong>{site.grievanceOfficer.name}</strong> — {site.grievanceOfficer.email}<br />
        We aim to acknowledge and respond within {site.grievanceOfficer.responseHours}.
      </p>

      <h2>11. Cookies</h2>
      <p>
        Our website uses only the cookies necessary for the site to function.
        We do not use tracking or advertising cookies.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        notified by email or on this page.
      </p>
    </LegalLayout>
  );
}
